import { clarity } from 'clarity-js';
import {
  AddPaymentInfoModel,
  AnalyticsEventModel,
  AnalyticsEventType,
  AnalyticsGeneralEventModel,
  BeginTransactionModel,
  BelenderUnavailabeModel,
  ClarityTagType,
  ContinueEntryModel,
  ContinueLDataModel,
  ContinuePaymentModel,
  ContinuePDataModel,
  ContinueShippingModel,
  ContinueSignatureModel,
  GenerateLeadModel,
  InvalidOcrModel,
  PinErrorBelModel,
  PurchaseModel,
  SendLoanRefusedEvent,
  SendOcrModel,
  SimulationModel,
  ValidOcrModel,
} from '../../interfaces/Analytics';
import { eventLogger } from '../Firebase';
import InsightService from '../Insight';

const AnalyticsService = {
  // ********************************************************************************************************************
  // CLARITY SESSION TAGS
  // ********************************************************************************************************************
  setDocumentTypeTag: (document_type: string) => {
    clarity.set(ClarityTagType.document_type, document_type);
  },

  // ********************************************************************************************************************
  // GENERAL EVENTS
  // ********************************************************************************************************************
  sendTryExit: (step_number: AnalyticsGeneralEventModel['step_number']) => {
    eventLogger(AnalyticsEventType.try_exit, {
      step_number,
    });
  },
  sendBackExit: (step_number: AnalyticsGeneralEventModel['step_number']) => {
    eventLogger(AnalyticsEventType.back_exit, {
      step_number,
    });
  },
  sendEventKo: (step_number: AnalyticsGeneralEventModel['step_number']) => {
    eventLogger(AnalyticsEventType.event_ko, {
      step_number,
    });
    InsightService.logEvent(AnalyticsEventType.event_ko, [{ step_number }]);
    clarity.event(AnalyticsEventType.event_ko, JSON.stringify(step_number));
  },
  sendLoanRefused: ({ denial_step }: SendLoanRefusedEvent) => {
    eventLogger(AnalyticsEventType.loan_refused, {
      denial_step,
    });
    InsightService.logEvent(AnalyticsEventType.loan_refused, [
      {
        denial_step,
      },
    ]);
  },
  sendResumption: ({
    flow_step,
    is_customer,
    application,
    product,
    amount,
  }: AnalyticsEventModel) => {
    eventLogger(AnalyticsEventType.resumption, {
      flow_step,
      is_customer,
      application,
      product,
      amount,
    });
  },
  // ********************************************************************************************************************
  // ENTRY EVENTS
  // ********************************************************************************************************************
  sendSimulation: ({
    simulation_amount,
    simulation_max_term,
    simulation_min_term,
    simulation_term_count,
    simulation_term,
    simulation_offer_count,
    simulation_cex_available,
    simulation_sus_available,
    simulation_pls_available,
  }: SimulationModel) => {
    eventLogger(AnalyticsEventType.simulation, {
      simulation_amount,
      simulation_max_term,
      simulation_min_term,
      simulation_term_count,
      simulation_term,
      simulation_offer_count,
      simulation_cex_available,
      simulation_sus_available,
      simulation_pls_available,
    });
    InsightService.logEvent(AnalyticsEventType.simulation, [
      {
        simulation_amount,
        simulation_max_term,
        simulation_min_term,
        simulation_term_count,
        simulation_term,
        simulation_offer_count,
        simulation_cex_available,
        simulation_sus_available,
        simulation_pls_available,
      },
    ]);
  },

  editSimulation: () => {
    eventLogger(AnalyticsEventType.edit_simulation);
  },

  changeSimulation: () => {
    eventLogger(AnalyticsEventType.change_simulation);
  },

  continueEntry: ({
    simulation_amount,
    simulation_term,
    simulation_tae,
    simulation_tin,
    simulation_regular_fee,
    simulation_first_fee,
    simulation_total_cost,
    simulation_opening_fee,
    simulation_opening_fee_type,
    simulation_flow_step,
    step_number,
    time_elapsed,
  }: ContinueEntryModel) => {
    eventLogger(AnalyticsEventType.continue_entry, {
      simulation_amount,
      simulation_term,
      simulation_tae,
      simulation_tin,
      simulation_regular_fee,
      simulation_first_fee,
      simulation_total_cost,
      simulation_opening_fee,
      simulation_opening_fee_type,
      simulation_flow_step,
      step_number,
      time_elapsed,
    });
    InsightService.logEvent(AnalyticsEventType.continue_entry, [
      {
        simulation_amount,
        simulation_term,
        simulation_tae,
        simulation_tin,
        simulation_regular_fee,
        simulation_first_fee,
        simulation_total_cost,
        simulation_opening_fee,
        simulation_opening_fee_type,
        simulation_flow_step,
        step_number,
        time_elapsed,
      },
    ]);
    clarity.event(AnalyticsEventType.continue_entry, 'origin');
  },

  beginTransaction: ({ currency, value, items }: BeginTransactionModel) => {
    eventLogger(AnalyticsEventType.begin_transaction, {
      currency,
      value,
      items,
    });
  },

  // ********************************************************************************************************************
  // PERSONAL DATA EVENTS
  // ********************************************************************************************************************

  backEntry: (time_elapsed: AnalyticsGeneralEventModel['time_elapsed']) => {
    eventLogger(AnalyticsEventType.back_entry, {
      time_elapsed,
    });
  },

  belenderUnavailable: ({ type }: BelenderUnavailabeModel) => {
    eventLogger(AnalyticsEventType.belender_unavailable, {
      type,
    });
    InsightService.logEvent(AnalyticsEventType.belender_unavailable, [
      {
        type,
      },
    ]);
  },

  continuePdata: ({
    cust_age_segment,
    cust_birth_country,
    cust_nationality,
    cust_email_domain,
    cust_classification,
    prop_require_documentation,
    prop_require_labor_data,
    prop_require_secure_payment,
    prop_require_require_address_data,
    prop_require_enrichment_personal_finances,
    prop_source_application,
    prop_product,
    prop_amount,
    prop_refinanced_amount,
    time_elapsed,
    step_number,
  }: ContinuePDataModel) => {
    eventLogger(AnalyticsEventType.continue_pdata, {
      cust_age_segment,
      cust_birth_country,
      cust_nationality,
      cust_email_domain,
      cust_classification,
      prop_require_documentation,
      prop_require_labor_data,
      prop_require_secure_payment,
      prop_require_require_address_data,
      prop_require_enrichment_personal_finances,
      prop_source_application,
      prop_product,
      prop_amount,
      prop_refinanced_amount,
      time_elapsed,
      step_number,
    });
    InsightService.logEvent(AnalyticsEventType.continue_pdata, [
      {
        cust_age_segment,
        cust_birth_country,
        cust_nationality,
        cust_email_domain,
        cust_classification,
        prop_require_documentation,
        prop_require_labor_data,
        prop_require_secure_payment,
        prop_require_require_address_data,
        prop_require_enrichment_personal_finances,
        prop_source_application,
        prop_product,
        prop_amount,
        prop_refinanced_amount,
        time_elapsed,
        step_number,
      },
    ]);
    clarity.event(AnalyticsEventType.continue_pdata, 'origin');
  },

  // ********************************************************************************************************************
  // LABOR DATA EVENTS
  // ********************************************************************************************************************

  continueLData: ({
    cust_contract_type,
    cust_monthly_income,
    cust_province,
    cust_postal_code,
    used_manual_input,
    time_elapsed,
    step_number,
  }: ContinueLDataModel) => {
    eventLogger(AnalyticsEventType.continue_ldata, {
      cust_contract_type,
      cust_monthly_income,
      cust_province,
      cust_postal_code,
      used_manual_input,
      time_elapsed,
      step_number,
    });
    InsightService.logEvent(AnalyticsEventType.continue_ldata, [
      {
        cust_contract_type,
        cust_monthly_income,
        cust_province,
        cust_postal_code,
        used_manual_input,
        time_elapsed,
        step_number,
      },
    ]);
    clarity.event(AnalyticsEventType.continue_ldata, 'origin');
  },

  manualInputAvailable: () => {
    eventLogger(AnalyticsEventType.manual_input_available);
    InsightService.logEvent(AnalyticsEventType.manual_input_available, []);
  },

  continueShipping: ({
    continue_mode,
    time_elapsed,
    step_number,
  }: ContinueShippingModel) => {
    eventLogger(AnalyticsEventType.continue_shipping, {
      continue_mode,
      time_elapsed,
      step_number,
    });
    InsightService.logEvent(AnalyticsEventType.continue_shipping, [
      {
        continue_mode,
        time_elapsed,
        step_number,
      },
    ]);
    clarity.event(AnalyticsEventType.continue_shipping, 'origin');
  },
  // ********************************************************************************************************************
  // OCR EVENTS
  // ********************************************************************************************************************

  continueOcr: ({ time_elapsed }: AnalyticsGeneralEventModel) => {
    eventLogger(AnalyticsEventType.continue_ocr, {
      time_elapsed,
    });
    InsightService.logEvent(AnalyticsEventType.continue_ocr, [
      { time_elapsed },
    ]);
  },

  sendOcr: ({ access_mode }: SendOcrModel) => {
    eventLogger(AnalyticsEventType.send_ocr, {
      access_mode,
    });
    InsightService.logEvent(AnalyticsEventType.send_ocr, [{ access_mode }]);
  },

  invalidOcr: ({ reason, third, device, time_elapsed }: InvalidOcrModel) => {
    eventLogger(AnalyticsEventType.invalid_ocr, {
      reason,
      third,
      device,
      time_elapsed,
    });
    InsightService.logEvent(AnalyticsEventType.invalid_ocr, [
      {
        reason,
        third,
        device,
        time_elapsed,
      },
    ]);
  },

  validOcr: ({ third, device, time_elapsed }: ValidOcrModel) => {
    eventLogger(AnalyticsEventType.valid_ocr, {
      third,
      device,
      time_elapsed,
    });
    InsightService.logEvent(AnalyticsEventType.valid_ocr, [
      {
        third,
        device,
        time_elapsed,
      },
    ]);
    clarity.event(AnalyticsEventType.valid_ocr, 'origin');
  },
  // ********************************************************************************************************************
  // BELENDER EVENTS
  // ********************************************************************************************************************

  showLegalBel: () => {
    eventLogger(AnalyticsEventType.show_legal_bel);
  },

  invalidPhoneBel: () => {
    eventLogger(AnalyticsEventType.invalid_phone_bel);
    InsightService.logEvent(AnalyticsEventType.invalid_phone_bel, []);
  },

  retryPhoneBel: ({ time_elapsed }: AnalyticsGeneralEventModel) => {
    eventLogger(AnalyticsEventType.retry_phone_bel, { time_elapsed });
    InsightService.logEvent(AnalyticsEventType.retry_phone_bel, [
      { time_elapsed },
    ]);
  },

  resendSmsBel: ({ time_elapsed }: AnalyticsGeneralEventModel) => {
    eventLogger(AnalyticsEventType.resend_sms_bel, { time_elapsed });
    InsightService.logEvent(AnalyticsEventType.resend_sms_bel, [
      { time_elapsed },
    ]);
  },

  pinErrorBel: ({ time_elapsed, attemp_number }: PinErrorBelModel) => {
    eventLogger(AnalyticsEventType.pin_error_bel, {
      time_elapsed,
      attemp_number,
    });
  },

  continueBel: ({ time_elapsed, attemp_number }: PinErrorBelModel) => {
    eventLogger(AnalyticsEventType.continue_bel, {
      time_elapsed,
      attemp_number,
    });
    InsightService.logEvent(AnalyticsEventType.continue_bel, [
      {
        time_elapsed,
        attemp_number,
      },
    ]);
    clarity.event(AnalyticsEventType.continue_bel, 'origin');
  },
  // ********************************************************************************************************************
  // TPV EVENTS
  // ********************************************************************************************************************

  addPaymentInfo: ({ currency, value, items }: AddPaymentInfoModel) => {
    eventLogger(AnalyticsEventType.add_payment_info, {
      currency,
      value,
      items,
    });
  },

  continuePayment: ({
    payment_type,
    has_previous_cards,
    amount,
    third,
    time_elapsed,
    step_number,
  }: ContinuePaymentModel) => {
    eventLogger(AnalyticsEventType.continue_payment, {
      payment_type,
      has_previous_cards,
      amount,
      third,
      time_elapsed,
      step_number,
    });
    InsightService.logEvent(AnalyticsEventType.continue_payment, [
      {
        payment_type,
        has_previous_cards,
        amount,
        third,
        time_elapsed,
        step_number,
      },
    ]);
    clarity.event(AnalyticsEventType.continue_payment, 'origin');
  },

  paymentError: () => {
    eventLogger(AnalyticsEventType.payment_error);
    InsightService.logEvent(AnalyticsEventType.payment_error, []);
  },

  // ********************************************************************************************************************
  // SIGNATURE EVENTS
  // ********************************************************************************************************************

  show_contract: () => {
    eventLogger(AnalyticsEventType.show_contract);
  },

  resendOtp: ({ time_elapsed }: AnalyticsGeneralEventModel) => {
    eventLogger(AnalyticsEventType.resend_otp, { time_elapsed });
    InsightService.logEvent(AnalyticsEventType.resend_otp, [{ time_elapsed }]);
  },

  pinError: () => {
    eventLogger(AnalyticsEventType.pin_error);
    InsightService.logEvent(AnalyticsEventType.pin_error, []);
  },

  continueSignature: ({
    time_elapsed,
    step_number,
    third,
  }: ContinueSignatureModel) => {
    eventLogger(AnalyticsEventType.continue_signature, {
      time_elapsed,
      step_number,
      third,
    });
    InsightService.logEvent(AnalyticsEventType.continue_signature, [
      {
        time_elapsed,
        step_number,
        third,
      },
    ]);
    clarity.event(AnalyticsEventType.continue_signature, 'origin');
  },
  // ********************************************************************************************************************
  // END EVENTS
  // ********************************************************************************************************************

  verify: () => {
    eventLogger(AnalyticsEventType.verify);
    InsightService.logEvent(AnalyticsEventType.verify, []);
    clarity.event(AnalyticsEventType.verify, 'origin');
  },

  purchase: ({ currency, value, items, transaction_id }: PurchaseModel) => {
    eventLogger(AnalyticsEventType.purchase, {
      currency,
      value,
      items,
      transaction_id,
    });
    InsightService.logEvent(AnalyticsEventType.purchase, [
      {
        currency,
        value,
        items,
        transaction_id,
      },
    ]);
    clarity.event(AnalyticsEventType.purchase, 'origin');
  },

  generateLead: ({ currency, value }: GenerateLeadModel) => {
    eventLogger(AnalyticsEventType.generate_lead, {
      currency,
      value,
    });
    InsightService.logEvent(AnalyticsEventType.generate_lead, [
      {
        currency,
        value,
      },
    ]);
    clarity.event(AnalyticsEventType.generate_lead, 'origin');
  },
};

export default AnalyticsService;
